import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, useOutsideClickEffect } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonCore,
  ButtonIcon,
  Icon,
  Loader,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';

import BulkActionFooter from '../../../components/BulkActionFooter';
import PopoverProgressBreakdown, {
  BY_DECISION,
  calculatePercentage,
  DONE,
  PROGRESS,
  TOTAL,
} from '../../../components/CertsDetails/PopoverProgressBreakdown';
import { LinearProgressBar } from '../../../components/Common';
import ReviewAndSubmit from '../../../components/ReviewAndSubmit';
import { getProgressBreakdownDetails } from '../../../redux/certdetails/action';
import {
  PERCENTAGE_100,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { executeBackNavigation, getMessageKey } from '../../../utils/utils';

import './CertsFooterActionBar.css';

function CertsFooterActionBar() {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isProgressBarOpen, setIsProgressBarOpen] = useState(false);
  const [percentage, setPercentage] = useState(0);
  const popoverRef = useRef(null);

  const { messages } = useSelector((state) => state?.user);
  const {
    bulkAction: { bulkFooterInfo, isBulkFooterVisible },
    core: { isRefreshSuccess },
    global: { backNavigation, certKey, mediaQueryValue },
    progressBreakdowndetails: {
      byDecisionDetails,
      errorMessage,
      initialLoadError,
    },
    reviewAndSubmit: { shouldShowReviewAndSubmitButton },
    sidePanel: { isSidePanelVisible },
  } = useSelector((state) => state?.certifications?.details);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

  const footerNotice =
    messages.CERTS_FOOTER_NOTICE ||
    localize(intl, getMessageKey('CERTS_FOOTER_NOTICE'));

  const footerClasses = classnames(
    'CertsFooterActionBar',
    isSidePanelVisible && 'CertsFooterActionBar--isSidePanelVisible',
    isBulkFooterVisible &&
      bulkFooterInfo &&
      'CertsFooterActionBar--isBulkAction',
    isProgressBarOpen && 'CertsFooterActionBar--isProgressBarOpen'
  );
  const footerPhase1 = classnames(
    'CertsFooterActionBar-phases',
    isBulkFooterVisible &&
      bulkFooterInfo &&
      'CertsFooterActionBar-phases1--isSidePanelVisible'
  );
  const footerPhase2 = classnames(
    'CertsFooterActionBar-phases',
    isBulkFooterVisible &&
      bulkFooterInfo &&
      'CertsFooterActionBar-phases2--isSidePanelVisible'
  );
  const progressMessageClasses = classnames(
    'CertsFooterActionBar-progressMessage',
    isRefreshSuccess
      ? 'CertsFooterActionBar-progressMessage--isVisible'
      : 'CertsFooterActionBar-progressMessage--isHidden',
    percentage === PERCENTAGE_100 &&
      'CertsFooterActionBar-progressMessage-showLabel'
  );

  useOutsideClickEffect(popoverRef, () => {
    if (!isMobileWidth) setIsProgressBarOpen(false);
  });

  const fetchProgessBarData = () => {
    dispatch(getProgressBreakdownDetails(certKey, BY_DECISION, true));
  };

  const refreshProgressData = () => {
    if (certKey) fetchProgessBarData();
  };

  useEffect(() => {
    if (byDecisionDetails && byDecisionDetails?.Summary) {
      setPercentage(
        calculatePercentage(
          byDecisionDetails.Summary[DONE],
          byDecisionDetails.Summary[TOTAL]
        )
      );
    }
  }, [byDecisionDetails, errorMessage, initialLoadError]);

  useEffect(() => {
    if (certKey) {
      fetchProgessBarData();
    }
  }, [certKey]);

  const handlePopOver = () => {
    if (!initialLoadError) {
      setIsProgressBarOpen((prev) => !prev);
    }
  };

  const toggleHoverClass = (isHovered) => {
    const progressBar = document.querySelector(
      '.CertsFooterActionBar-progressBar'
    );

    if (progressBar) {
      if (isHovered) {
        progressBar.classList.add(
          'CertsFooterActionBar-progressBar--isHovered'
        );
      } else {
        progressBar.classList.remove(
          'CertsFooterActionBar-progressBar--isHovered'
        );
      }
    }
  };

  return (
    <Box
      tag='footer'
      className={footerClasses}
      dataTestId='cert-details-footer'>
      <Box tag='div' className={footerPhase1}>
        <Button
          type='button'
          kind='subtle'
          className='CertsFooterActionBar-backBtn'
          size='medium'
          onClick={() => executeBackNavigation(backNavigation)}>
          {messages.CERTS_BACK || localize(intl, getMessageKey('CERTS_BACK'))}
        </Button>
        <Box className='CertsFooterActionBar-progressBar'>
          <div
            className='CertsFooterActionBar-progress-content'
            ref={popoverRef}>
            {initialLoadError ? (
              <Box className='CertsFooterActionBar-progressFailure'>
                <Typography
                  kind='body2-bold'
                  className='CertsFooterActionBar-progressErrorMessage'>
                  Error while loading progress bar breakdown data.
                </Typography>
                <ButtonIcon
                  size='large'
                  kind='ghost'
                  icon={<Icon kind='Refresh' size='medium' color='navy-900' />}
                  onClick={refreshProgressData}
                />
              </Box>
            ) : (
              <>
                {isProgressBarOpen && (
                  <PopoverProgressBreakdown
                    closePopover={handlePopOver}
                    setIsProgressBarOpen={setIsProgressBarOpen}
                    isProgressBarOpen={isProgressBarOpen}
                    isMobileWidth={isMobileWidth}
                    label={
                      messages['Progress.v2.label.ATTESTATIONS'] ||
                      localize(
                        intl,
                        getMessageKey('Progress.v2.label.ATTESTATIONS')
                      )
                    }
                  />
                )}
                {byDecisionDetails?.Summary ? (
                  <>
                    <Box
                      kind='body2'
                      className='CertsFooterActionBar-linearProgess'>
                      <LinearProgressBar value={percentage} />
                    </Box>
                    <Typography
                      kind='body2-bold'
                      className='CertsFooterActionBar-progressPercentage'>
                      {`${byDecisionDetails.Summary[PROGRESS]}%`}
                    </Typography>
                    <Icon
                      kind='ChevronUpSmall'
                      size='medium'
                      rotate={isProgressBarOpen ? '180' : null}
                    />
                  </>
                ) : (
                  !errorMessage && (
                    <Loader
                      kind='dots'
                      format='inline'
                      color='brand'
                      size='medium'
                    />
                  )
                )}
                <ButtonCore
                  onMouseEnter={() => toggleHoverClass(true)}
                  onMouseLeave={() => toggleHoverClass(false)}
                  onClick={handlePopOver}
                  className='CertsFooterActionBar-togglePopover'
                />
              </>
            )}
          </div>
        </Box>
        <Box tag='div' className='CertsFooterActionBar-submit'>
          <Typography kind='body2' className={progressMessageClasses}>
            {footerNotice}
          </Typography>
          {shouldShowReviewAndSubmitButton && <ReviewAndSubmit />}
        </Box>
      </Box>
      <Box tag='div' className={footerPhase2}>
        <BulkActionFooter />
      </Box>
    </Box>
  );
}

export default CertsFooterActionBar;
